import { useQueryClient } from "@tanstack/react-query";
import { Info, Pencil } from "lucide-react";
import { type FormEvent, Fragment, type KeyboardEvent, useEffect, useState } from "react";
import { useBlocker, useOutletContext, useSearchParams } from "react-router";
import { COUNTRIES, PASSWORD_MIN_LENGTH } from "../../../shared/constants";
import { normalizeProfile, PROFILE_LABELS, type PublicProfile, profileSchema } from "../../../shared/profile";
import { passwordChangeSchema } from "../../../shared/schemas";
import { ApiRequestError, api } from "../../api/client";
import { keys, menuApi, openPreview, useProfile } from "../../api/queries";
import { useAuth } from "../../auth/AuthContext";
import { useAuthOptions } from "../../auth/useAuthOptions";
import { Bar, Modal } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, SelectField, TextField } from "../../components/ui";
import { plural } from "../../lib/format";
import type { ShellContext } from "../AppShell";
import { HoursEditor } from "./HoursEditor";
import { LocationsTab } from "./LocationsTab";
import { DataTab, TeamTab } from "./TeamTab";

const TABS = [
  ["restaurant", "Restaurant"],
  ["locations", "Locații"],
  ["region", "Regional"],
  ["account", "Cont"],
  ["team", "Echipă"],
  ["notif", "Notificări"],
  ["data", "Date și cont"],
] as const;
type Tab = (typeof TABS)[number][0];

/** Setări (prototype page `settings`): restaurant profile, region, account, team, e-mails, data and account. */
export function SettingsPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("tab") === "account" ? "account" : "restaurant");
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const i = TABS.findIndex((t) => t[0] === tab);
    const next =
      TABS[(i + (e.key === "ArrowRight" ? 1 : -1) + TABS.length) % TABS.length]?.[0] ?? "restaurant";
    setTab(next);
    setTimeout(() => document.getElementById(`st-${next}`)?.focus(), 0);
  };
  return (
    <>
      <h1 className="h1">Setări</h1>
      {/* Phones: one native picker instead of a row of tabs that scrolls sideways. */}
      <div className="field mobile-only">
        <label className="field-label" htmlFor="set-pick">
          Secțiunea
        </label>
        <select
          id="set-pick"
          className="field-input"
          value={tab}
          onChange={(e) => setTab(e.target.value as Tab)}
        >
          {TABS.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="tabs-bar desktop-only" role="tablist" aria-label="Secțiuni setări" onKeyDown={onKey}>
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`st-${id}`}
            aria-selected={tab === id}
            aria-controls="set-panel"
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <div id="set-panel" role="tabpanel" aria-labelledby={`st-${tab}`} className="stack" style={{ gap: 20 }}>
        {tab === "restaurant" && <RestaurantTab />}
        {tab === "locations" && <LocationsTab />}
        {tab === "region" && <RegionTab />}
        {tab === "account" && <AccountTab />}
        {tab === "team" && <TeamTab />}
        {tab === "data" && <DataTab />}
        {tab === "notif" && (
          <section className="card stack" style={{ gap: 12 }}>
            <h2 className="h2" style={{ margin: 0 }}>
              E-mailurile pe care le primești
            </h2>
            <p className="muted" style={{ margin: 0 }}>
              Meniova trimite doar mesaje despre contul și meniul tău, fără newslettere. Ele ajung la e-mailul
              contului; facturile ajung la e-mailul din datele de facturare.
            </p>
            <ul className="stack" style={{ gap: 8, margin: 0, paddingLeft: 20 }}>
              <li>Confirmarea adresei și resetarea parolei, când le ceri.</li>
              <li>
                Perioada gratuită: cu 3 zile înainte de final, în ziua în care se încheie și dacă meniul se
                închide pentru clienți.
              </li>
              <li>Factura fiscală, după fiecare plată.</li>
              <li>
                O dată pe săptămână, cât timp produse din meniul public au alergenii neconfirmați: care sunt
                și unde îi confirmi.
              </li>
              <li>Confirmarea cererii de rambursare, dacă o trimiți.</li>
            </ul>
            <p className="caption muted" style={{ margin: 0 }}>
              Aceste mesaje sunt necesare pentru cont și nu se pot opri.
            </p>
          </section>
        )}
      </div>
    </>
  );
}

/** Country, currency, time zone and menu language: shown as they are; they cannot be changed in the app. */
function RegionTab() {
  const { membership } = useOutletContext<ShellContext>();
  const r = membership.restaurant;
  const rows: [string, string][] = [
    ["Țara", COUNTRIES.find(([code]) => code === r.country)?.[1] ?? r.country],
    ["Moneda prețurilor", r.currency === "EUR" ? "euro (EUR)" : "lei (RON)"],
    ["Fusul orar", r.timezone === "Europe/Bucharest" ? "Ora României (Europe/Bucharest)" : r.timezone],
    ["Limba meniului", r.sourceLang === "ro" ? "română" : r.sourceLang],
  ];
  return (
    <section className="card stack" aria-labelledby="reg-h" style={{ gap: 12 }}>
      <h2 id="reg-h" className="h2">
        Regiune
      </h2>
      <dl className="kv-list">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="caption muted" style={{ margin: 0 }}>
        Acestea se aleg la crearea restaurantului și nu se pot schimba din aplicație. Prețurile nu se
        convertesc niciodată automat. Dacă ai nevoie de o schimbare, scrie-ne din pagina de ajutor.
      </p>
    </section>
  );
}

type FieldDef = {
  k: keyof PublicProfile;
  group?: string;
  hint: string;
  area?: boolean;
  full?: boolean;
  type?: string;
  max: number;
  ph?: string;
  ac?: string;
  help?: string;
};

const FIELDS: FieldDef[] = [
  { k: "name", group: "Identitate", hint: "(obligatoriu)", max: 80, ac: "organization" },
  { k: "country", hint: "", max: 0 },
  {
    k: "descRo",
    hint: "(opțional)",
    area: true,
    full: true,
    max: 200,
    help: "Apare sub numele restaurantului în meniul public.",
  },
  { k: "descEn", hint: "(opțional)", area: true, full: true, max: 200 },
  { k: "address", group: "Contact", hint: "(opțional)", max: 120, ac: "street-address" },
  { k: "city", hint: "(opțional)", max: 120, ac: "address-level2" },
  { k: "phone", hint: "(opțional)", type: "tel", max: 40, ph: "+40 …", ac: "tel" },
  { k: "email", hint: "(opțional)", type: "email", max: 120, ac: "email" },
  { k: "website", group: "Linkuri", hint: "(opțional)", type: "url", max: 300, ph: "https://" },
  { k: "facebook", hint: "(opțional)", type: "url", max: 300, ph: "https://facebook.com/…" },
  { k: "instagram", hint: "(opțional)", type: "url", max: 300, ph: "https://instagram.com/…" },
  {
    k: "google",
    hint: "(opțional)",
    type: "url",
    max: 300,
    ph: "https://",
    help: "Apare în meniul public pentru toți clienții, indiferent de nota din feedback.",
  },
  { k: "tripadvisor", hint: "(opțional)", type: "url", max: 300, ph: "https://" },
  { k: "hours", group: "Program", hint: "(opțional)", full: true, max: 400 },
  {
    k: "wifiName",
    group: "Wi-Fi pentru clienți",
    hint: "(opțional)",
    max: 60,
    help: "Apare sus în meniul public, cu un buton care copiază parola.",
  },
  { k: "wifiPass", hint: "(opțional)", max: 63 },
  {
    k: "announceRo",
    group: "Anunț în meniu",
    hint: "(opțional)",
    area: true,
    full: true,
    max: 200,
    help: "De exemplu: „Luni–Vineri 12–15: meniul zilei 35 lei”. Apare sus în meniu, între datele alese.",
  },
  { k: "announceEn", hint: "(opțional)", area: true, full: true, max: 200 },
  { k: "announceFrom", hint: "(opțional)", type: "date", max: 10 },
  { k: "announceTo", hint: "(opțional)", type: "date", max: 10 },
];

function RestaurantTab() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const profile = useProfile(rid);
  const qc = useQueryClient();
  const { refresh: refreshMe } = useAuth();
  const toast = useToast();
  const api2 = menuApi(rid);
  const [form, setForm] = useState<PublicProfile | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const saved = profile.data?.draft ?? null;
  const values = form ?? saved;
  const dirty =
    !!form && !!saved && JSON.stringify(normalizeProfile(form)) !== JSON.stringify(normalizeProfile(saved));
  const blocker = useBlocker(dirty);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (profile.isLoading || !values || !profile.data) {
    return (
      <p className="muted" role="status">
        Se încarcă datele restaurantului…
      </p>
    );
  }
  const p = profile.data;

  async function reload() {
    await Promise.all([qc.invalidateQueries({ queryKey: keys.profile(rid) }), refreshMe()]);
  }

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!form) return;
    const v = profileSchema.safeParse(form);
    if (!v.success) {
      const errs: Record<string, string> = {};
      for (const i of v.error.issues) errs[i.path.join(".")] ??= i.message;
      setErrors(errs);
      setTimeout(() => document.querySelector<HTMLElement>('#set-panel [aria-invalid="true"]')?.focus(), 30);
      return;
    }
    setBusy(true);
    try {
      await api2.saveProfile(v.data);
      setForm(null);
      setErrors({});
      await reload();
      toast("Setări salvate. Datele publice rămân ciornă până le publici din fila Restaurant.");
    } catch (err) {
      if (err instanceof ApiRequestError) setErrors(err.fields);
      toast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const n = Object.keys(errors).length;
  const set = (k: keyof PublicProfile, value: string) => {
    setForm({ ...(values as PublicProfile), [k]: value });
    if (errors[k]) setErrors(({ [k]: _, ...rest }) => rest);
  };

  return (
    <>
      <SearchVisibility />
      {dirty && (
        <Bar sticky tone="warning" icon={Pencil} title="Ai modificări nesalvate">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setForm(null);
              setErrors({});
            }}
          >
            Renunță
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => save()}>
            Salvează
          </button>
        </Bar>
      )}
      {p.changes.length > 0 && (
        <Bar
          tone="info"
          icon={Info}
          title="Datele publice ale restaurantului au modificări nepublicate."
          sub={`Clienții văd încă versiunea publicată. ${plural(p.changes.length, "câmp modificat", "câmpuri modificate")}: ${p.changes.map((c) => c.label).join(", ")}.`}
        >
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openPreview(rid)}>
            Previzualizează
          </button>
          <button type="button" className="btn-link" onClick={() => setDiscardOpen(true)}>
            Renunță
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={dirty}
            onClick={() => setPublishOpen(true)}
          >
            Publică datele restaurantului
          </button>
        </Bar>
      )}
      {n > 0 && (
        <Alert tone="error" live>
          Formularul are {plural(n, "problemă", "probleme")}. Verifică câmpurile marcate.
        </Alert>
      )}
      <section className="card stack" aria-labelledby="set-h" style={{ gap: 16 }}>
        <div className="stack" style={{ gap: 4 }}>
          <h2 id="set-h" className="h2">
            Informații despre restaurant
          </h2>
          <span className="page-sub" style={{ lineHeight: "20px" }}>
            Tot ce completezi aici apare în meniul public, la final: numele, descrierea, adresa, telefonul,
            e-mailul și linkurile. După salvare rămân ciornă până apeși „Publică datele restaurantului”.
            Datele contului nu sunt publice.
          </span>
        </div>
        <form className="form-grid" onSubmit={save} noValidate>
          {FIELDS.map((f) => {
            const id = `set-r-${f.k}`;
            const common = { id, label: PROFILE_LABELS[f.k], hint: f.hint, error: errors[f.k], help: f.help };
            return (
              <Fragment key={f.k}>
                {f.group && <h3 className="full form-group-h">{f.group}</h3>}
                <div className={f.full ? "full" : undefined} style={{ minWidth: 0 }}>
                  {f.k === "hours" ? (
                    <HoursEditor
                      value={values.hours}
                      onChange={(v) => set("hours", v)}
                      error={errors.hours}
                    />
                  ) : f.k === "country" ? (
                    <SelectField
                      {...common}
                      options={COUNTRIES}
                      value={values.country}
                      onChange={(e) => set("country", e.target.value)}
                    />
                  ) : f.area ? (
                    <div className="field">
                      <label className="field-label" htmlFor={id}>
                        {PROFILE_LABELS[f.k]} <span className="field-hint">{f.hint}</span>
                      </label>
                      <textarea
                        id={id}
                        className="field-input"
                        rows={2}
                        maxLength={f.max}
                        value={values[f.k]}
                        aria-describedby={f.help ? `${id}-help` : undefined}
                        onChange={(e) => set(f.k, e.target.value)}
                      />
                      {f.help && (
                        <span id={`${id}-help`} className="field-help">
                          {f.help}
                        </span>
                      )}
                    </div>
                  ) : (
                    <TextField
                      {...common}
                      type={f.type ?? "text"}
                      maxLength={f.max}
                      placeholder={f.ph}
                      autoComplete={f.ac ?? "off"}
                      value={values[f.k]}
                      onChange={(e) => set(f.k, e.target.value)}
                    />
                  )}
                </div>
              </Fragment>
            );
          })}
          <div className="full row" style={{ justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!dirty}
              onClick={() => {
                setForm(null);
                setErrors({});
              }}
            >
              Renunță
            </button>
            <button type="submit" className="btn btn-primary" disabled={!dirty || busy}>
              Salvează
            </button>
          </div>
        </form>
      </section>

      {discardOpen && (
        <Modal
          title="Renunți la modificările nepublicate?"
          body="Datele restaurantului revin la versiunea pe care o văd acum clienții. Modificările nepublicate se pierd."
          alert
          onClose={() => setDiscardOpen(false)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setDiscardOpen(false)}>
                Păstrează
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api2.discardProfile(p.revision);
                    setForm(null);
                    await reload();
                    toast("Datele publice au revenit la versiunea publicată.");
                  } catch (err) {
                    toast(err instanceof Error ? err.message : String(err));
                    await reload();
                  } finally {
                    setBusy(false);
                    setDiscardOpen(false);
                  }
                }}
              >
                Renunță la modificări
              </button>
            </>
          }
        />
      )}
      {publishOpen && (
        <Modal
          title="Publici datele restaurantului?"
          body="Numele, descrierea, adresa, telefonul, e-mailul și linkurile salvate devin vizibile clienților imediat. Meniurile și aspectul nu se schimbă."
          onClose={() => setPublishOpen(false)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPublishOpen(false)}>
                Renunță
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-autofocus=""
                onClick={async () => {
                  try {
                    await api2.publishProfile(p.revision);
                    setPublishOpen(false);
                    await reload();
                    toast("Datele restaurantului au fost publicate.");
                  } catch (err) {
                    setPublishOpen(false);
                    await reload();
                    toast(err instanceof Error ? err.message : String(err));
                  }
                }}
              >
                Publică
              </button>
            </>
          }
        >
          <ul className="diff-list">
            {p.changes.map((c) => (
              <li key={c.field}>
                <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span className="sub">{c.label}</span>
                  <span className="chg">
                    {c.from || "—"} → {c.to || "—"}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Modal>
      )}

      {blocker.state === "blocked" && (
        <Modal
          alert
          title="Ai modificări nesalvate în Setări"
          body="Dacă pleci acum, modificările nu se păstrează."
          onClose={() => blocker.reset()}
          actions={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                data-autofocus=""
                onClick={() => blocker.reset()}
              >
                Rămâi în Setări
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  setForm(null);
                  blocker.proceed();
                }}
              >
                Renunță la modificări
              </button>
            </>
          }
        />
      )}
    </>
  );
}

function AccountTab() {
  const { me } = useOutletContext<ShellContext>();
  const toast = useToast();
  const options = useAuthOptions();
  const [params] = useSearchParams();
  const [linking, setLinking] = useState(false);

  async function linkGoogle() {
    setLinking(true);
    try {
      const { url } = await api<{ url: string }>("POST", "/auth/google/link");
      window.location.assign(url);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
      setLinking(false);
    }
  }
  const [pw, setPw] = useState({ cur: "", n1: "", n2: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const v = passwordChangeSchema.safeParse({ currentPassword: pw.cur, newPassword: pw.n1 });
    if (!v.success) for (const i of v.error.issues) errs[i.path.join(".")] ??= i.message;
    if (!errs.newPassword && pw.n2 !== pw.n1) errs.confirm = "Parolele nu coincid.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await api("POST", "/auth/password/change", { currentPassword: pw.cur, newPassword: pw.n1 });
      setPw({ cur: "", n1: "", n2: "" });
      toast("Parola a fost schimbată. Celelalte sesiuni au fost deconectate.");
    } catch (err) {
      if (err instanceof ApiRequestError && Object.keys(err.fields).length) setErrors(err.fields);
      else toast(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="card stack" style={{ gap: 16 }} aria-labelledby="acct-h">
        <div className="stack" style={{ gap: 4 }}>
          <h2 id="acct-h" className="h2">
            Datele contului
          </h2>
          <span className="page-sub">
            Schimbarea numelui și a e-mailului de autentificare se activează într-un pas următor.
          </span>
        </div>
        <dl className="grid-2" style={{ margin: 0 }}>
          <div>
            <dt className="caption muted">Numele tău</dt>
            <dd style={{ margin: 0, fontWeight: 600 }}>{me.user.name}</dd>
          </div>
          <div>
            <dt className="caption muted">E-mail de autentificare</dt>
            <dd style={{ margin: 0, fontWeight: 600, overflowWrap: "anywhere" }}>{me.user.email}</dd>
          </div>
        </dl>
      </section>
      {(options?.googleEnabled || me.user.googleLinked) && (
        <section className="card stack" style={{ gap: 12 }} aria-labelledby="google-h">
          <h2 id="google-h" className="h2">
            Autentificare cu Google
          </h2>
          {params.get("google") === "linked" && (
            <Alert tone="success" live>
              Contul Google a fost conectat. De acum poți intra și cu „Continuă cu Google”.
            </Alert>
          )}
          {me.user.googleLinked ? (
            <span className="muted" style={{ fontSize: 14 }}>
              Contul tău Google este conectat: poți intra și cu „Continuă cu Google”.
            </span>
          ) : (
            <>
              <span className="muted" style={{ fontSize: 14 }}>
                Conectează contul Google ca să intri fără parolă. Îți alegi contul pe pagina Google.
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ alignSelf: "flex-start" }}
                disabled={linking}
                onClick={linkGoogle}
              >
                {linking ? "Se deschide Google…" : "Conectează contul Google"}
              </button>
            </>
          )}
        </section>
      )}
      <section className="card stack" style={{ gap: 16 }} aria-labelledby="pw-h">
        <h2 id="pw-h" className="h2">
          Schimbă parola
        </h2>
        {me.user.hasPassword === false ? (
          <span className="muted" style={{ fontSize: 14 }}>
            Contul a fost creat cu Google și nu are parolă. Poți seta una din „Ai uitat parola?” pe pagina de
            autentificare, când trimiterea e-mailurilor este activă.
          </span>
        ) : (
          <form className="stack" onSubmit={changePassword} noValidate style={{ maxWidth: 420 }}>
            <TextField
              id="pw-cur"
              label="Parola actuală"
              type="password"
              autoComplete="current-password"
              value={pw.cur}
              onChange={(e) => setPw({ ...pw, cur: e.target.value })}
              error={errors.currentPassword}
            />
            <TextField
              id="pw-n1"
              label={`Parola nouă (minimum ${PASSWORD_MIN_LENGTH} caractere)`}
              type="password"
              autoComplete="new-password"
              value={pw.n1}
              onChange={(e) => setPw({ ...pw, n1: e.target.value })}
              error={errors.newPassword}
            />
            <TextField
              id="pw-n2"
              label="Confirmă parola nouă"
              type="password"
              autoComplete="new-password"
              value={pw.n2}
              onChange={(e) => setPw({ ...pw, n2: e.target.value })}
              error={errors.confirm}
            />
            <button
              type="submit"
              className="btn btn-primary"
              style={{ alignSelf: "flex-start" }}
              disabled={busy}
            >
              Schimbă parola
            </button>
          </form>
        )}
      </section>
      <section className="card stack" style={{ gap: 12 }} aria-labelledby="ses-h">
        <h2 id="ses-h" className="h2">
          Sesiuni
        </h2>
        <span className="page-sub">
          Dacă ai intrat în cont pe un telefon sau calculator pe care nu îl mai folosești.
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ alignSelf: "flex-start" }}
          onClick={async () => {
            const r = await api<{ revoked: number }>("POST", "/auth/sessions/revoke-others");
            toast(
              r.revoked === 1
                ? "1 sesiune deconectată."
                : r.revoked
                  ? `${r.revoked} sesiuni deconectate.`
                  : "Nu există alte sesiuni deschise.",
            );
          }}
        >
          Deconectează celelalte sesiuni
        </button>
      </section>
    </>
  );
}

/**
 * „Arată meniul în Google” (owner 2026-11-04): on by default; applies at once (it is not menu content). Only the
 * owner changes it; closed menus are never indexed anyway.
 */
function SearchVisibility() {
  const { membership } = useOutletContext<ShellContext>();
  const r = membership.restaurant;
  const [on, setOn] = useState(r.searchVisible);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const owner = membership.role === "owner";
  async function toggle(next: boolean) {
    setBusy(true);
    setError("");
    try {
      await api("PUT", `/restaurants/${r.id}/search`, { visible: next });
      setOn(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setBusy(false);
  }
  return (
    <section className="card stack" style={{ gap: 8 }} aria-labelledby="seo-h">
      <h2 id="seo-h" className="h2" style={{ margin: 0 }}>
        Meniul în Google
      </h2>
      <label className="check-row">
        <input
          type="checkbox"
          checked={on}
          disabled={!owner || busy}
          onChange={(e) => void toggle(e.target.checked)}
        />
        <span>
          Arată meniul în căutările Google. Oamenii care caută restaurantul găsesc meniul, cu adresa,
          programul și prețurile. Se aplică imediat.
        </span>
      </label>
      {error && (
        <Alert tone="error" live>
          {error}
        </Alert>
      )}
    </section>
  );
}
